import { X } from "@mui/icons-material";
import { GiCheckMark } from "react-icons/gi";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { RiLoader2Fill } from "react-icons/ri";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../pics/SponsaLogo.svg";
import { setSignUpdata, setLogin } from "../../State/authSlicer";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../../api/api";
import apisecured from "../../api/apisecured";
import { debounce } from "lodash";
import { RxCross2 } from "react-icons/rx";

const ReferalSignUp = () => {
  // --- State ---
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    role: "",
    referralCode: "",
    codeStatus: "idle", // idle, checking, valid, invalid
    codeReason: "",
    customUrl: "",
    urlStatus: "idle", // idle, checking, available, taken
    displayName: "",
    about: "",
    profilePic: null, // preview base64
    profilePicFile: null, // actual File to upload
    tosAccepted: false,
    privacyAccepted: false,
  });

  const [loadingStates, setLoadingStates] = useState({
    isCheckingCode: false,
    isCheckingUrl: false,
    isCreatingAccount: false,
  });

  const fileInputRef = useRef(null);

  const stepLabels = [
    "Sign In",
    "Choose Role",
    "Claim URL",
    "Profile Picture",
    "About You",
    "Confirm",
  ];

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const auth = useSelector((s) => s.auth);
  const { IsGoogleAccount } = useSelector((s) => s.auth);

  // --- Google Sign In ---
  const handleGoogleSignIn = () => {
    window.location.href = API + "account/signinwithgoogle";
  };

  // --- Read redirect params after googlecallback ---
  useEffect(() => {
    const qs = new URLSearchParams(location.search);

    const step = qs.get("step");
    const email = qs.get("email");
    const googleID = qs.get("googleID");

    // If backend redirected here after googlecallback for a NEW user:
    if (step === "2" && email && googleID) {
      dispatch(
        setSignUpdata({
          email: decodeURIComponent(email),
          password: "Custom@4329",
          IsGoogleAccount: true,
          GoogleID: googleID,
        }),
      );

      setCurrentStep(2);

      // Clean URL (prevents re-trigger on refresh)
      navigate("/signup", { replace: true });
    }
  }, [location.search, dispatch, navigate]);

  // --- Role selection -> update local + redux ---
  const setRole = (role) => {
    setFormData((p) => ({
      ...p,
      role,
      ...(role === "supporter" ? { referralCode: "", codeStatus: "idle" } : {}),
    }));

    dispatch(
      setSignUpdata({
        accountType: role === "creator" ? 1 : 2,
      }),
    );
  };

  // --- Referral code check (UI-side simulation for now) ---
  // IMPORTANT: replace this later with real backend validation endpoint if/when available.
  const referralAbortRef = useRef(null);

  const validateReferralCodeDebounced = useRef(
    debounce(async (code) => {
      if (referralAbortRef.current) {
        referralAbortRef.current.abort();
      }

      referralAbortRef.current = new AbortController();

      if (!code || code.length < 4) {
        setFormData((p) => ({
          ...p,
          codeStatus: "idle",
          codeReason: "",
        }));
        setLoadingStates((p) => ({ ...p, isCheckingCode: false }));
        return;
      }

      try {
        setLoadingStates((p) => ({ ...p, isCheckingCode: true }));

        const res = await fetch(
          `${API}account/referral/validate?code=${encodeURIComponent(code)}`,
          { signal: referralAbortRef.current.signal },
        );

        const data = await res.json();

        setFormData((p) => ({
          ...p,
          codeStatus: data.isValid ? "valid" : "invalid",
          codeReason: data.reason || "",
        }));
      } catch (err) {
        if (err.name === "AbortError") return;

        setFormData((p) => ({
          ...p,
          codeStatus: "invalid",
          codeReason: "Unable to validate referral code",
        }));
      } finally {
        setLoadingStates((p) => ({ ...p, isCheckingCode: false }));
      }
    }, 500),
  ).current;

  const validateReferralCode = (value) => {
    const upper = value.toUpperCase();

    setFormData((p) => ({
      ...p,
      referralCode: upper,
      codeStatus: upper.length >= 4 ? "checking" : "idle",
      codeReason: "",
    }));

    validateReferralCodeDebounced(upper);
  };

  // --- Username / URL check (real API: account/CheckUsername) ---
  const checkUsernameExists = async (nextUsername) => {
    try {
      const response = await apisecured.get("account/CheckUsername", {
        params: {
          username: nextUsername,
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Username check error:", error);

      return {
        success: false,
        data: error.response?.data || error.message,
      };
    }
  };

  const checkUsernameDebounced = useMemo(
    () =>
      debounce(async (nextUsername) => {
        const value = nextUsername?.trim();

        if (!value || value.length < 3) {
          setFormData((p) => ({
            ...p,
            urlStatus: "idle",
            urlReason: "",
          }));
          return;
        }

        setFormData((p) => ({
          ...p,
          urlStatus: "checking",
          urlReason: "",
        }));

        const result = await checkUsernameExists(value);

        if (result.success) {
          setFormData((p) => ({
            ...p,
            urlStatus: result.data?.available === true ? "available" : "taken",
            urlReason:
              result.data?.message ||
              (result.data?.available === true
                ? "Username is available"
                : "Username already taken"),
          }));
        } else {
          setFormData((p) => ({
            ...p,
            urlStatus: "taken",
            urlReason:
              result.data?.errorMessage?.replace(/_/g, " ") ||
              "Unable to check username",
          }));
        }
      }, 500),
    [],
  );

  useEffect(() => {
    return () => {
      checkUsernameDebounced.cancel?.();
    };
  }, [checkUsernameDebounced]);

  // --- Profile upload (store File + preview) ---
  const handleProfileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFormData((prev) => ({ ...prev, profilePicFile: file }));

    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData((prev) => ({
        ...prev,
        profilePic: event.target.result, // preview
      }));
    };
    reader.readAsDataURL(file);
  };

  // --- Can proceed rules ---
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return true; // only google sign in button
      case 2:
        if (formData.role === "creator") return formData.codeStatus === "valid";
        return formData.role === "supporter";
      case 3:
        return (
          formData.customUrl.length >= 3 && formData.urlStatus === "available"
        );
      case 4:
        return true; // optional
      case 5:
        return formData.displayName.trim().length > 0;
      case 6:
        return formData.tosAccepted && formData.privacyAccepted;
      default:
        return true;
    }
  };

  // --- Create account (Step 6) ---
  const createAccount = useCallback(async () => {
    try {
      setLoadingStates((p) => ({ ...p, isCreatingAccount: true }));

      // Basic guard
      if (!auth?.email) {
        toast.error("Missing email. Please sign in again.");
        setCurrentStep(1);
        return;
      }

      // Push all final fields to redux (optional but keeps state consistent)
      dispatch(
        setSignUpdata({
          sponsaUserName: formData.customUrl,
          displayName: formData.displayName,
          bio: formData.about,
        }),
      );

      const form = new FormData();
      form.append("Email", auth.email);
      form.append("Password", auth.password || "Custom@4329");

      // accountType from redux (setRole sets it)
      if (!auth.accountType) {
        toast.error("Please select a role.");
        setCurrentStep(2);
        return;
      }
      form.append("AccountType", String(auth.accountType));
      if (Number(auth.accountType) === 1) {
        if (!formData.referralCode || formData.codeStatus !== "valid") {
          toast.error(
            formData.codeReason || "Please enter a valid referral code.",
          );
          setCurrentStep(2);
          return;
        }

        form.append("ReferralCode", formData.referralCode);
      }
      form.append("SponsaUserName", formData.customUrl);
      form.append("DisplayName", formData.displayName);
      form.append("Bio", formData.about || "");

      // If backend requires image: add if present
      if (formData.profilePicFile) {
        form.append("Image", formData.profilePicFile);
      }

      // Google
      const endpoint = IsGoogleAccount
        ? "account/CreateGoogleUser"
        : "account/CreateUser";

      if (IsGoogleAccount && auth.GoogleID) {
        form.append("GoogleID", auth.GoogleID);
      }

      const res = await fetch(API + endpoint, {
        method: "POST",
        body: form,
        redirect: "follow",
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = (json?.errorMessage || "An error occurred").replace(
          /_/g,
          " ",
        );
        toast.error(msg);

        // If referral errors, send back to step 2
        // if (json?.errorMessage?.includes("REFERRAL")) setCurrentStep(2);

        return;
      }

      toast.success("Account created successfully.");

      // Google signup -> auto login (matches your old flow)
      if (endpoint === "account/CreateGoogleUser") {
        try {
          const { data: loginData } = await apisecured.post("account/login", {
            email: auth.email,
            password: auth.password || "Custom@4329",
          });

          dispatch(
            setLogin({
              email: loginData.email,
              uid: loginData.uid,
              displayName: loginData.displayName,
              sponsaUserName: loginData.userName,
              userImg: loginData.imageURL,
              CoverIMG: loginData.coverImageUrl,
              accountType: loginData.accountType,
              YtUrl: loginData.ytUrl,
              TwitterUrl: loginData.twitterUrl,
              InstaUrl: loginData.instaUrl,
              FbUrl: loginData.fbUrl,
              bio: loginData.bio,
              stripeID: loginData.stripeID,
              role: loginData.role,
              IsGoogleAccount: loginData.IsGoogleAccount,
              currency: loginData.currency,
              paypalID: loginData.payPalID,
              paypalConnectedEmail: loginData.payPalConnectedEmail,
              ReceiveAllTips: loginData.receiveAllTips,
              ReceiveGoalFunded: loginData.receiveGoalFunded,
              socialLinks: loginData.socialLinks || [],
              selectedTags: loginData.selectedTags || [],
              isYouTubeConnected: loginData.isYoutubeConnected,
              youtubeEmail: loginData.youtubeEmail,
              isTwitchConnected: loginData.isTwitchConnected,
              twitchEmail: loginData.twitchEmail,
              isTwitterConnected: loginData.isTwitterConnected,
              twitterUsername: loginData.twitterUsername,
            }),
          );

          setTimeout(() => navigate("/dashboard/"), 800);
        } catch (e) {
          toast.error("Login failed after Google account creation.");
        }
      } else {
        // Normal account -> go OTP (same as old flow)
        setTimeout(() => {
          navigate("/signup/OTP-verification", {
            state: { email: auth.email },
          });
        }, 800);
      }
    } catch (e) {
      toast.error("An error occurred");
    } finally {
      setLoadingStates((p) => ({ ...p, isCreatingAccount: false }));
    }
  }, [
    auth?.email,
    auth?.password,
    auth?.accountType,
    auth?.GoogleID,
    IsGoogleAccount,
    dispatch,
    formData.customUrl,
    formData.displayName,
    formData.about,
    formData.profilePicFile,
    formData.referralCode,
    navigate,
  ]);

  // --- Next/Back ---
  const handleNext = async () => {
    if (currentStep < 6 && canProceed()) {
      // store url in redux when moving past step 3
      if (currentStep === 3) {
        dispatch(setSignUpdata({ sponsaUserName: formData.customUrl }));
      }

      setCurrentStep((prev) => prev + 1);
      return;
    }

    if (currentStep === 6 && canProceed()) {
      await createAccount();
    }
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-1 sm:gap-2 mb-6 lg:mb-8">
      {[1, 2, 3, 4, 5, 6].map((step) => (
        <React.Fragment key={step}>
          <div
            className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] sm:text-sm font-medium transition-all duration-300 
          ${
            step === currentStep
              ? "bg-[#00FF8F] text-[#030021] scale-110"
              : step < currentStep
                ? "bg-[#00FF8F]/30 text-[#00FF8F]"
                : "bg-white/10 text-white/40"
          }`}
          >
            {step < currentStep ? (
              <GiCheckMark
                size={10}
                className={`${
                  step === currentStep
                    ? "!fill-[#030021]"
                    : step < currentStep
                      ? "!fill-[#00FF8F]"
                      : "!fill-white/40"
                }`}
              />
            ) : (
              step
            )}
          </div>
          {step < 6 && (
            <div
              className={`h-[2px] w-4 sm:w-6 transition-all duration-300 ${
                step < currentStep ? "bg-[#00FF8F]/50" : "bg-white/10"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="bg-[#030021] min-h-screen flex flex-col items-center justify-center pt-[1.2rem] font-sans ">
      <ToastContainer />
      <div className="grid grid-cols-1 w-full  lg:max-w-[584px] mx-auto py-6 sm:py-8">
        <div className=" ">
          <Link to={"/"}>
            <img
              src={logo}
              alt=""
              className=" w-[160px] sm:w-[180px] mx-auto "
            />
          </Link>
          <div className=" md:px-8 px-0 w-full rounded-[20px]  ">
            <div className="mt-4">
              <div className=" bg-[#030021] text-white flex items-center justify-center sm:p-4 p-3 relative overflow-hidden ">
                {/* Background Blobs */}
                <div className="fixed top-1/4 -left-32 w-96 h-96 bg-[#00FF8F]/10 rounded-full blur-[64px] pointer-events-none" />
                <div className="fixed bottom-1/4 -right-32 w-96 h-96 bg-orange-500/10 rounded-full blur-[64px] pointer-events-none" />

                <div className="w-full max-w-[584px] z-10  ">
                  {/* Card */}
                  <div
                    className="bg-white/5  border py-5 sm:py-6 px-4 sm:px-6 backdrop-filter backdrop-blur-lg rounded-[16px]  border-solid border-[rgba(255,255,255,0.1)] p-[24px] [box-shadow:0_25px_50px_-12px_rgba(0,_0,_0,_0.5)]
                  "
                  >
                    <StepIndicator />

                    <div className="min-h-[280px] sm:min-h-[320px]">
                      {/* Step 1: Sign In */}
                      {currentStep === 1 && (
                        <div className="text-center animate-in fade-in duration-500">
                          <h2 className="text-xl sm:text-2xl font-semibold mb-2">
                            Welcome to SPONSA
                          </h2>
                          <p className="text-white/60 mb-6 sm:mb-8 text-sm sm:text-base">
                            Sign in to get started
                          </p>
                          <button
                            onClick={handleGoogleSignIn}
                            className="w-full flex items-center justify-center gap-2 sm:gap-3  bg-white text-gray-900 font-normal py-3 rounded-xl hover:bg-gray-100 transition-transform hover:scale-[1.02]"
                          >
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                              <path
                                fill="#4285F4"
                                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                              />
                              <path
                                fill="#34A853"
                                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                              />
                              <path
                                fill="#FBBC05"
                                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                              />
                              <path
                                fill="#EA4335"
                                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                              />
                            </svg>
                            Continue with Google
                          </button>
                          <p className="text-[12px] text-white/40 mt-6">
                            By continuing, you agree to SPONSA's terms of use
                          </p>
                        </div>
                      )}

                      {/* Step 2: Role */}
                      {currentStep === 2 && (
                        <div className="animate-in slide-in-from-right-4 duration-300">
                          <h2 className="text-xl sm:text-2xl font-semibold mb-2">
                            How will you use SPONSA?
                          </h2>
                          <p className="text-white/60 mb-6">
                            Choose the option that best describes you
                          </p>

                          <div className="space-y-3">
                            <div
                              onClick={() => setRole("creator")}
                              className={`sm:px-4 px-3 sm:py-3.5 py-2.5 rounded-xl border-2 cursor-pointer transition-all ${
                                formData.role === "creator"
                                  ? "border-[#00FF8F] bg-[#00FF8F]/10"
                                  : "border-white/10 bg-white/5 hover:border-white/20"
                              }`}
                            >
                              <div className="flex items-center gap-2 sm:gap-3 ">
                                <div className="sm:w-10 sm:h-10 w-8 h-8 sm:rounded-lg rounded-md bg-white/10 flex items-center justify-center sm:text-xl text-md ">
                                  🎨
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium">
                                    I'm a Creator
                                  </div>
                                  <div className="sm:text-sm text-xs text-white/50">
                                    I want to receive tips from my audience
                                  </div>
                                </div>
                                {formData.role === "creator" && (
                                  <div className="sm:w-5 sm:h-5 h-4 w-4 rounded-full bg-[#00FF8F] text-[#030021] flex items-center justify-center">
                                    <GiCheckMark size={10} strokeWidth={4} />
                                  </div>
                                )}
                              </div>
                            </div>

                            <div
                              onClick={() => setRole("supporter")}
                              className={`sm:px-4 px-3 sm:py-3.5 py-2.5 rounded-xl border-2 cursor-pointer transition-all ${
                                formData.role === "supporter"
                                  ? "border-[#00FF8F] bg-[#00FF8F]/10"
                                  : "border-white/10 bg-white/5 hover:border-white/20"
                              }`}
                            >
                              <div className="flex items-center gap-2 sm:gap-3 ">
                                <div className="sm:w-10 sm:h-10 w-8 h-8 sm:rounded-lg rounded-md bg-white/10 flex items-center justify-center sm:text-xl text-md ">
                                  💜
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium">
                                    I'm a Supporter
                                  </div>
                                  <div className="sm:text-sm text-xs text-white/50">
                                    I want to tip my favourite creators
                                  </div>
                                </div>
                                {formData.role === "supporter" && (
                                  <div className="sm:w-5 sm:h-5 h-4 w-4 rounded-full bg-[#00FF8F] text-[#030021] flex items-center justify-center">
                                    <GiCheckMark size={10} strokeWidth={4} />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {formData.role === "creator" && (
                            <div className="mt-6 pt-6 border-t border-white/10 animate-in fade-in">
                              <label className="block sm:text-sm text-xs font-medium text-white/80 mb-2">
                                Enter your referral code
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={formData.referralCode}
                                  onChange={(e) =>
                                    validateReferralCode(e.target.value)
                                  }
                                  placeholder="e.g. PENNY-A7X3"
                                  className={`w-full bg-white/5 border-2 rounded-xl sm:p-3 px-3 py-2 outline-none transition-all ${
                                    formData.codeStatus === "valid"
                                      ? "border-[#00FF8F]"
                                      : formData.codeStatus === "invalid"
                                        ? "border-red-500"
                                        : "border-white/10 focus:border-[#00FF8F]"
                                  }`}
                                />
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                  {loadingStates.isCheckingCode && (
                                    <RiLoader2Fill
                                      size={18}
                                      className="animate-spin text-[#00FF8F]"
                                    />
                                  )}
                                  {!loadingStates.isCheckingCode &&
                                    formData.codeStatus === "valid" && (
                                      <div className="sm:w-5 sm:h-5 h-4 w-4 rounded-full bg-[#00FF8F] text-[#030021] flex items-center justify-center">
                                        <GiCheckMark
                                          size={10}
                                          strokeWidth={4}
                                        />
                                      </div>
                                    )}
                                  {!loadingStates.isCheckingCode &&
                                    formData.codeStatus === "invalid" && (
                                      <RxCross2
                                        size={18}
                                        className="text-red-500"
                                      />
                                    )}
                                </div>
                              </div>
                              {formData.codeStatus === "invalid" && (
                                <p className="text-red-500 text-sm mt-2">
                                  {formData.codeReason}
                                </p>
                              )}

                              {formData.codeStatus === "valid" && (
                                <p className="text-[#00FF8F] text-sm mt-2">
                                  {formData.codeReason}
                                </p>
                              )}

                              <p className="sm:text-xs text-[9px] text-white/40 mt-2 ">
                                Don't have a code? Select "I'm a Supporter" to
                                continue without one.
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Step 3: Claim URL */}
                      {currentStep === 3 && (
                        <div className="animate-in slide-in-from-right-4 duration-300">
                          <h2 className="text-xl sm:text-2xl font-semibold mb-2">
                            Claim your URL
                          </h2>
                          <p className="text-white/60 mb-6">
                            Choose a unique URL for your SPONSA page
                          </p>
                          <div
                            className={`flex items-center bg-white/5 border-2 rounded-xl overflow-hidden transition-all ${
                              formData.urlStatus === "available"
                                ? "border-[#00FF8F]"
                                : formData.urlStatus === "taken"
                                  ? "border-red-500"
                                  : "border-white/10 focus-within:border-[#00FF8F]"
                            }`}
                          >
                            <span className="pl-3 text-white/50 text-sm">
                              sponsa.app/
                            </span>
                            <input
                              type="text"
                              value={formData.customUrl}
                              onChange={(e) => {
                                const clean = e.target.value
                                  .replace(/[^a-zA-Z0-9_-]/g, "")
                                  .toLowerCase();
                                setFormData((p) => ({
                                  ...p,
                                  customUrl: clean,
                                }));
                                checkUsernameDebounced(clean);
                              }}
                              className="flex-1 bg-transparent p-3 outline-none"
                              placeholder="yourname"
                            />

                            <div className="pr-3">
                              {loadingStates.isCheckingUrl && (
                                <RiLoader2Fill
                                  size={18}
                                  className="animate-spin text-[#00FF8F]"
                                />
                              )}
                              {!loadingStates.isCheckingUrl &&
                                formData.urlStatus === "available" && (
                                  <div className="sm:w-5 sm:h-5 h-4 w-4 rounded-full bg-[#00FF8F] text-[#030021] flex items-center justify-center">
                                    <GiCheckMark size={10} strokeWidth={4} />
                                  </div>
                                )}
                              {!loadingStates.isCheckingUrl &&
                                formData.urlStatus === "taken" && (
                                  <RxCross2
                                    size={18}
                                    className="text-red-500"
                                  />
                                )}
                            </div>
                          </div>
                          {formData.urlStatus === "available" && (
                            <div className="mt-4 p-3 rounded-lg bg-[#00FF8F]/10 border border-[#00FF8F]/30 text-[#00FF8F] text-sm">
                              ✓ Your page will be at sponsa.app/
                              {formData.customUrl}
                            </div>
                          )}
                          {formData.urlStatus === "taken" && (
                            <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 text-sm">
                              ✕ This URL is already taken.
                            </div>
                          )}
                          <p className=" mt-[12px] text-[12px] text-[rgba(255,_255,_255,_0.4)]   ">
                            • Between 3-30 characters
                          </p>
                        </div>
                      )}

                      {/* Step 4: Profile Pic */}
                      {currentStep === 4 && (
                        <div className="text-center animate-in slide-in-from-right-4 duration-300">
                          <h2 className="text-xl sm:text-2xl font-semibold mb-2">
                            Add a profile picture
                          </h2>
                          <p className="text-white/60 mb-6 sm:mb-8 text-sm sm:text-base">
                            Help your supporters recognize you
                          </p>
                          <div className="flex flex-col items-center">
                            <div
                              onClick={() => fileInputRef.current?.click()}
                              className="w-32 h-32 rounded-full border-4 border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-[#00FF8F]/50 overflow-hidden bg-white/10 transition-all"
                            >
                              {formData.profilePic ? (
                                <img
                                  src={formData.profilePic}
                                  alt="Profile"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="text-white/40 flex flex-col items-center">
                                  <div className="text-[30px] mb-[4px]">📷</div>
                                  <span className="text-[12px]">
                                    Click to Upload
                                  </span>
                                </div>
                              )}
                            </div>
                            <input
                              type="file"
                              ref={fileInputRef}
                              className="hidden"
                              accept="image/*"
                              onChange={handleProfileUpload}
                            />
                            {formData.profilePic && (
                              <button
                                onClick={() =>
                                  setFormData((p) => ({
                                    ...p,
                                    profilePic: null,
                                    profilePicFile: null,
                                  }))
                                }
                                className="text-red-500 text-sm mt-4 hover:text-red-400"
                              >
                                Remove photo
                              </button>
                            )}
                            <p className=" max-w-[300px] mx-auto mt-[32px] text-[12px] text-[rgba(255,_255,_255,_0.4)]   ">
                              Recommended: Square image, at least 400x400 pixels
                              Supports JPG, PNG (max 5MB)
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Step 5: About */}
                      {currentStep === 5 && (
                        <div className="animate-in slide-in-from-right-4 duration-300">
                          <h2 className="text-xl sm:text-2xl font-semibold mb-2">
                            Tell us about yourself
                          </h2>
                          <p className="text-white/60 mb-6">
                            This will appear on your SPONSA page
                          </p>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-white/80 mb-2">
                                Display Name
                              </label>
                              <input
                                type="text"
                                className="w-full bg-white/5 border-2 border-white/10 rounded-xl px-3 py-2.5 outline-none focus:border-[#00FF8F] placeholder:text-white/40 "
                                placeholder="How should we call you?"
                                value={formData.displayName}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    displayName: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-white/80 mb-2">
                                About{" "}
                                <span className="text-white/30 font-normal">
                                  (optional)
                                </span>
                              </label>
                              <textarea
                                rows="4"
                                className="w-full bg-white/5 border-2 border-white/10 rounded-xl px-3 py-2.5 outline-none focus:border-[#00FF8F] resize-none  placeholder:text-white/40"
                                placeholder={
                                  formData.role === "creator"
                                    ? "Tell supporters what you create..."
                                    : "Tell us a bit about yourself..."
                                }
                                maxLength={2000}
                                value={formData.about}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    about: e.target.value,
                                  }))
                                }
                              />
                              <p className="text-right text-[10px] text-white/30 mt-1">
                                {formData.about.length}/2000
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Step 6: Confirm */}
                      {currentStep === 6 && (
                        <div className="animate-in slide-in-from-right-4 duration-300">
                          <h2 className="text-xl sm:text-2xl font-semibold mb-2">
                            Almost there!
                          </h2>
                          <p className="text-white/60 mb-6">
                            Review and confirm to complete your account
                          </p>

                          <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-6">
                            <div className="flex items-center gap-4 mb-4">
                              <div className="w-16 h-16 rounded-full bg-white/10 overflow-hidden flex items-center justify-center">
                                {formData.profilePic ? (
                                  <img
                                    src={formData.profilePic}
                                    alt="Summary"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span>👤</span>
                                )}
                              </div>
                              <div>
                                <div className="font-bold text-lg">
                                  {formData.displayName || "Your Name"}
                                </div>
                                <div className="text-[#00FF8F] text-sm">
                                  sponsa.app/{formData.customUrl}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  formData.role === "creator"
                                    ? "bg-[#00FF8F]/20 text-[#00FF8F]"
                                    : "bg-purple-500/20 text-purple-400"
                                }`}
                              >
                                {formData.role === "creator"
                                  ? "🎨 Creator"
                                  : "💜 Supporter"}
                              </span>
                              {formData.role === "creator" && (
                                <span className="text-white/30 text-xs">
                                  via referral
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="space-y-4">
                            <label className="flex items-start gap-3 cursor-pointer group">
                              <div className="relative mt-1">
                                <input
                                  type="checkbox"
                                  className="sr-only"
                                  checked={formData.tosAccepted}
                                  onChange={(e) =>
                                    setFormData((p) => ({
                                      ...p,
                                      tosAccepted: e.target.checked,
                                    }))
                                  }
                                />
                                <div
                                  className={`w-5 h-5 flex items-center justify-center border-2 rounded transition-colors ${
                                    formData.tosAccepted
                                      ? "bg-[#00FF8F] border-[#00FF8F]"
                                      : "border-white/30"
                                  }`}
                                >
                                  {formData.tosAccepted && (
                                    <GiCheckMark
                                      size={10}
                                      className="text-[#030021]"
                                      strokeWidth={4}
                                    />
                                  )}
                                </div>
                              </div>
                              <span className="text-sm text-white/70">
                                I agree to the{" "}
                                <span className="text-[#00FF8F] underline">
                                  Terms of Service
                                </span>
                              </span>
                            </label>

                            <label className="flex items-start gap-3 cursor-pointer group">
                              <div className="relative mt-1">
                                <input
                                  type="checkbox"
                                  className="sr-only"
                                  checked={formData.privacyAccepted}
                                  onChange={(e) =>
                                    setFormData((p) => ({
                                      ...p,
                                      privacyAccepted: e.target.checked,
                                    }))
                                  }
                                />
                                <div
                                  className={`w-5 h-5 flex items-center justify-center border-2 rounded transition-colors ${
                                    formData.privacyAccepted
                                      ? "bg-[#00FF8F] border-[#00FF8F]"
                                      : "border-white/30"
                                  }`}
                                >
                                  {formData.privacyAccepted && (
                                    <GiCheckMark
                                      size={10}
                                      className="text-[#030021]"
                                      strokeWidth={4}
                                    />
                                  )}
                                </div>
                              </div>
                              <span className="text-sm text-white/70">
                                I agree to the{" "}
                                <span className="text-[#00FF8F] underline">
                                  Privacy Policy
                                </span>
                              </span>
                            </label>
                          </div>
                          <p className="  mt-[32px] text-[12px] text-[rgba(255,_255,_255,_0.4)]   ">
                            By creating an account, you confirm you are at least
                            18 years old.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Navigation Buttons */}
                    {currentStep > 1 && (
                      <div className="flex gap-2 sm:gap-3 mt-6 sm:mt-8 animate-in fade-in duration-700  ">
                        <button
                          onClick={() => setCurrentStep((prev) => prev - 1)}
                          className="flex-1 py-2.5 sm:py-3 font-medium border-2 border-white/10 rounded-xl hover:bg-white/5 transition-colors text-sm"
                          disabled={loadingStates.isCreatingAccount}
                        >
                          Back
                        </button>
                        <button
                          onClick={handleNext}
                          disabled={
                            !canProceed() || loadingStates.isCreatingAccount
                          }
                          className="flex-1 sm:px-6 px-2 py-2.5 sm:py-3 font-normal bg-[#00FF8F] text-[#030021] rounded-xl hover:bg-[#00e680] transition-colors disabled:cursor-not-allowed flex items-center justify-center gap-2 disabled:bg-[rgba(255,_255,_255,_0.1)] disabled:text-[rgba(255,_255,_255,_0.3)]  "
                        >
                          {loadingStates.isCreatingAccount && (
                            <RiLoader2Fill
                              size={18}
                              className="animate-spin text-[#030021]"
                            />
                          )}
                          {currentStep === 6
                            ? "Create Account"
                            : "Continue"}{" "}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Footer Label */}
                  <p className="text-center text-[11px] sm:text-[12px] text-white/40 mt-5 sm:mt-6 ">
                    Step {currentStep} of 6: {stepLabels[currentStep - 1]}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferalSignUp;
